import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import { X } from 'lucide-react';
import { toast } from '../../../../../ui/toast';
import emailOpenImage from '../../../../../assets/images/graphics/email-open-logo.svg';
import {
  deleteAssessmentUser,
  getAssessmentUsers,
  inviteUserToAssessment,
  updateAssessmentUserStatus,
} from '../../../../../utils/api_service/assessment_api';
import { addUsersToAssessmentWithFile } from '../../../../../utils/api_service/user_api';
import { Permission } from '../../../../../utils/helpers/permissions';
import PermissionGate from '../../../../../utils/hooks/PermissionGate';
import {
  AssessmentUser,
  InviteUserRequest,
  User,
  UserAssessmentResponse,
} from '../../../../../utils/types/assessment_types';
import { AssessmentUserRow } from './assessment_user_row/assessment_user_row';
import { InviteUserModal } from './invite_user_modal/invite_user_modal';
import { UserSearch } from './user_search/user_search';

type AssessmentUsersProps = {
  assessmentId: string;
};

export const AssessmentUsers: React.FC<AssessmentUsersProps> = ({
  assessmentId,
}) => {
  const [assignedUsers, setAssignedUsers] = useState<AssessmentUser[]>([]);
  const [inviteModal, toggleInviteModal] = useState<boolean>(false);

  useEffect(() => {
    if (assignedUsers.length === 0) {
      getAssessmentUsers(assessmentId).then((response) => {
        if (response.length > 0) {
          const formatted: AssessmentUser[] = response.map(
            (item: UserAssessmentResponse) => ({
              assessmentUserId: item.id,
              userId: item.userId,
              firstName: item.user.firstName,
              lastName: item.user.lastName,
              email: item.user.email,
              score: item.score,
              status: item.status,
              enabled: item.enabled,
            }),
          );
          setAssignedUsers(formatted);
        }
      });
    }
  }, [assessmentId, assignedUsers.length]);

  const addAssessmentUser = (user: User) => {
    let inviteUserDetails: InviteUserRequest = {
      assessmentId: assessmentId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    };
    inviteUserToAssessment(inviteUserDetails).then((response) => {
      if (response.id) {
        let assessmentUser: AssessmentUser = {
          assessmentUserId: response.id,
          userId: response.userId,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          email: response.user.email,
          score: response.score,
          status: response.status,
          enabled: response.enabled,
        };
        setAssignedUsers((prev) => [...prev, assessmentUser]);
        toast.success('Successfully added user!', {
          position: 'bottom-center',
        });
      } else {
        toast.error(response.message, { position: 'bottom-center' });
      }
    });
  };

  const inviteUserForAssessment = (inviteUserDetails: InviteUserRequest) => {
    inviteUserDetails.assessmentId = assessmentId;
    inviteUserToAssessment(inviteUserDetails).then((response) => {
      if (response.id) {
        let assessmentUser: AssessmentUser = {
          assessmentUserId: response.id,
          userId: response.userId,
          firstName: inviteUserDetails.firstName,
          lastName: inviteUserDetails.lastName,
          email: inviteUserDetails.email,
          score: response.score,
          status: response.status,
          enabled: response.enabled,
        };
        setAssignedUsers((prev) => [...prev, assessmentUser]);
        toggleInviteModal(false);
        toast.success('Successfully invited new user!', {
          position: 'bottom-center',
        });
      } else {
        toggleInviteModal(false);
        toast.error(response.error, { position: 'bottom-center' });
      }
    });
  };

  const inviteUsersFromCSV = (csvFile: File) => {
    addUsersToAssessmentWithFile(csvFile, assessmentId)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
      })
      .then((response) => {
        if (!response) return;
        const newUsers: AssessmentUser[] = [];
        response.forEach((addUserResponse: any) => {
          if (
            !assignedUsers.some(
              (user) => user.userId === addUserResponse.user.id,
            )
          ) {
            newUsers.push({
              assessmentUserId: addUserResponse.id,
              userId: addUserResponse.userId,
              firstName: addUserResponse.user.firstName,
              lastName: addUserResponse.user.lastName,
              email: addUserResponse.user.email,
              score: addUserResponse.score,
              status: addUserResponse.status,
              enabled: addUserResponse.enabled,
            });
          }
        });
        setAssignedUsers((prev) => [...prev, ...newUsers]);
        toggleInviteModal(false);
        toast.success('Successfully added users!', {
          position: 'bottom-center',
        });
      });
  };

  const deleteUserFromAssessment = (assessmentUser: AssessmentUser) => {
    deleteAssessmentUser(assessmentUser.assessmentUserId).then((response) => {
      if (response.ok) {
        setAssignedUsers((prev) =>
          prev.filter(
            (item) => item.assessmentUserId !== assessmentUser.assessmentUserId,
          ),
        );
      }
    });
  };

  const changeUserActivityStatus = (assessmentUser: AssessmentUser) => {
    updateAssessmentUserStatus(
      assessmentUser.assessmentUserId,
      !assessmentUser.enabled,
    ).then((response) => {
      if (response.ok) {
        setAssignedUsers((prev) =>
          prev.map((user) =>
            user.userId === assessmentUser.userId
              ? { ...user, enabled: !user.enabled }
              : user,
          ),
        );
      }
    });
  };

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <PermissionGate requiredPermissions={[Permission.ASSESSMENTS_EDIT]}>
        <UserSearch
          displayInviteModal={toggleInviteModal}
          assignedUsers={assignedUsers}
          addAssessmentUser={addAssessmentUser}
        />
      </PermissionGate>

      <Typography variant="h6" fontWeight={700} sx={{ mb: 2, mt: 1 }}>
        List of Candidates
      </Typography>

      {assignedUsers.length > 0 && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  Employee Id
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  Name
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  Email
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  Score
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  Status
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignedUsers.map((user) => (
                <AssessmentUserRow
                  key={user.email}
                  assessmentUser={user}
                  deleteUserFromAssessment={deleteUserFromAssessment}
                  changeUserActivityStatus={changeUserActivityStatus}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {assignedUsers.length === 0 && (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={3}
          sx={{ py: 8 }}
        >
          <Box
            component="img"
            src={emailOpenImage}
            alt="email-verify"
            sx={{ width: 160, opacity: 0.9 }}
          />
          <Button
            onClick={() => toggleInviteModal(true)}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            Invite Members
          </Button>
        </Stack>
      )}

      <Dialog
        open={inviteModal}
        onClose={() => toggleInviteModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, p: 1 } }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Invite Candidates
          </Typography>
          <IconButton
            size="small"
            onClick={() => toggleInviteModal(false)}
            aria-label="close"
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderBottom: 'none' }}>
          <InviteUserModal
            inviteUserForAssessment={inviteUserForAssessment}
            inviteUsersFromCSV={inviteUsersFromCSV}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
